// Sales Pipeline: the funnel from the HubSpot Deals export. The API returns count and value
// per Funnel Stage per Reporting Month; everything derived (totals, running totals, conversion
// rates) is worked out here, so every chart in the section agrees.

import { compact, formatCount, lineChartOption, monthLabel, palette } from "./chartTheme";

/** Funnel Stages, in order. Lead and Contacted show no value: it starts at Demo. */
export const FUNNEL_STAGES = [
  { key: "lead", label: "Lead", hasValue: false },
  { key: "qualified", label: "Contacted", hasValue: false },
  { key: "demo", label: "Demo", hasValue: true },
  { key: "proposal", label: "Proposal", hasValue: true },
  { key: "negotiation", label: "Negotiation", hasValue: true },
  { key: "closed", label: "Closed Won", hasValue: true },
];

export const MEASURE_OPTIONS = [
  { value: "count", text: "Count" },
  { value: "value", text: "Value" },
];

export const UPLOAD_EXTENSIONS = [".xlsx", ".csv"];
/** The API's limit, checked here too so a too-big file fails before it's sent. */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** An export older than this is flagged as stale. */
export const STALE_AFTER_DAYS = 35;

const DAY_MS = 24 * 60 * 60 * 1000;

/** True when an upload time is more than STALE_AFTER_DAYS ago. Never uploaded is not "stale". */
export function isStale(iso, now = new Date()) {
  if (!iso) return false;
  return now.getTime() - new Date(iso).getTime() > STALE_AFTER_DAYS * DAY_MS;
}

/** Why a file can't be uploaded, checked before sending it; empty when it's fine. */
export function fileProblem(file) {
  if (!file) return "Choose a file to upload.";
  const name = file.name.toLowerCase();
  if (!UPLOAD_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return "Upload the HubSpot export as an .xlsx or .csv file.";
  }
  if (file.size > MAX_UPLOAD_BYTES) return `The file is too large. The limit is ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`;
  return "";
}

/** The API's message for a failed upload, with its detail (e.g. which header is missing). */
export function uploadErrorMessage(err) {
  if (err && err.code === "ECONNABORTED") return "The upload took too long. Please try again.";
  const res = err && err.response;
  if (res && res.status === 403) return "You don't have permission to upload HubSpot exports.";
  const data = (res && res.data) || {};
  if (!data.message) return "The upload failed. Please try again.";
  return data.detail ? `${data.message} ${data.detail}` : data.message;
}

/** Below this share of deals with a value, value figures get a note. */
export const LOW_VALUE_FILL_RATE = 0.8;

/** HubSpot's company currency: deal values come from "Amount in company currency", already converted to it. */
export const VALUE_CURRENCY = "USD";

const VALUE_FORMAT = { style: "currency", currency: VALUE_CURRENCY, maximumFractionDigits: 0 };

/** A deal value in whole units: 1234 → "US$1,234". */
export function formatValue(value) {
  return value == null ? "–" : new Intl.NumberFormat("en-GB", VALUE_FORMAT).format(value);
}

/** A deal value for a chart axis: 1200000 → "US$1.2M". */
export function compactValue(value) {
  return new Intl.NumberFormat("en-GB", { ...VALUE_FORMAT, notation: "compact", maximumFractionDigits: 1 }).format(value);
}

/** 12.345 → "12%" (or "12.3%" with digits = 1); null → "–". */
export function formatPercent(value, digits = 0) {
  return value == null ? "–" : `${value.toFixed(digits)}%`;
}

/** part ÷ whole as a percentage; null when whole is 0. */
function percent(part, whole) {
  return whole > 0 ? (part / whole) * 100 : null;
}

/** Count and value per Funnel Stage, added up over the given months. Lead and Contacted keep a null value. */
export function stageTotals(months) {
  const totals = {};
  FUNNEL_STAGES.forEach((s) => {
    totals[s.key] = { count: 0, value: s.hasValue ? 0 : null };
  });
  months.forEach((m) =>
    FUNNEL_STAGES.forEach((s) => {
      const figures = m.stages[s.key];
      totals[s.key].count += figures.count;
      if (s.hasValue) totals[s.key].value += figures.value || 0;
    })
  );
  return totals;
}

/**
 * The 12-month funnel summary: KPI figures and one row per Funnel Stage, from counts summed over
 * every month. Step % is this stage's count ÷ the previous stage's. Closed Lost follows Closed Won
 * but isn't a Funnel Stage: deals drop out at any stage, so it has no Step %, and the API sends no
 * value for it.
 */
export function funnelSummary(months) {
  const totals = stageTotals(months);
  const leads = totals.lead.count;
  const largest = Math.max(...FUNNEL_STAGES.map((s) => totals[s.key].count));
  const rows = FUNNEL_STAGES.map((s, i) => {
    const { count, value } = totals[s.key];
    const previous = i > 0 ? totals[FUNNEL_STAGES[i - 1].key].count : null;
    return {
      key: s.key,
      label: s.label,
      count,
      value,
      barPercent: largest > 0 ? (count / largest) * 100 : 0,
      stepPercent: i === 0 ? null : percent(count, previous),
    };
  });
  const closedLost = months.reduce(
    (sum, m) => sum + Object.values(m.lostReasons || {}).reduce((a, count) => a + count, 0),
    0
  );
  rows.push({
    key: "closed_lost",
    label: "Closed Lost",
    count: closedLost,
    value: null,
    barPercent: largest > 0 ? (closedLost / largest) * 100 : 0,
    stepPercent: null,
  });
  return {
    rows,
    leads,
    closed: totals.closed.count,
    closedValue: totals.closed.value,
    leadToClosed: percent(totals.closed.count, leads),
  };
}

/** Why deals were lost, in the order the API lists them. */
const LOST_REASON_LABELS = {
  no_response: "Went silent / no response",
  pricing: "Pricing or FX rates",
  other_provider: "Already with another provider",
  product_gap: "Product or coverage gap",
  timing: "Bad timing / not a priority",
  not_a_fit: "Not a real fit",
  other: "Other",
  uncategorised: "Not categorised",
};

/**
 * Closed Lost deals per reason group, added up over every month, largest first with Not categorised
 * last. Share is of all Closed Lost deals. Groups with no deals are left out.
 */
export function lostReasonSummary(months, categories) {
  const counts = categories.map((key) => ({
    key,
    label: LOST_REASON_LABELS[key] || key,
    count: months.reduce((sum, m) => sum + ((m.lostReasons && m.lostReasons[key]) || 0), 0),
  }));
  const total = counts.reduce((sum, c) => sum + c.count, 0);
  const largest = Math.max(0, ...counts.map((c) => c.count));
  const rows = counts
    .filter((c) => c.count > 0)
    .sort((a, b) => (a.key === "uncategorised") - (b.key === "uncategorised") || b.count - a.count)
    .map((c) => ({ ...c, share: percent(c.count, total), barPercent: (c.count / largest) * 100 }));
  return { rows, total };
}

/**
 * Each stage's chart colour, as a slot in the Business Analytics palette, so a stage looks the same
 * on every Sales Pipeline chart. Closed takes the logo green. Lead is never drawn as a bar.
 */
const STAGE_SLOTS = { qualified: 3, demo: 1, proposal: 4, negotiation: 2, closed: 0 };

/**
 * The stages drawn as bars. Lead is left out of counts because it's larger and would flatten the
 * others; values start at Demo.
 */
export function chartStages(measure) {
  return FUNNEL_STAGES.filter((s) => (measure === "value" ? s.hasValue : s.key !== "lead"));
}

/**
 * A grouped bar chart over Reporting Months: one group per month, one bar per stage from
 * `chartStages(measure)`. The current month's bars are faded and its label says "so far". The
 * tooltip lists every stage for the hovered month, Lead included in count mode.
 *
 * @param {Object} opts
 * @param {Array<{ month, partial, stages }>} opts.months  per-stage figures, as the API returns them
 * @param {"count"|"value"} opts.measure
 * @returns {{ option: Object|null, table: { columns: string[], rows: Array } }}
 */
export function stageBarChart({ months, measure }) {
  if (!months.length) return { option: null, table: { columns: [], rows: [] } };
  const colors = palette();
  const drawn = chartStages(measure);
  const listed = measure === "value" ? drawn : FUNNEL_STAGES;
  const format = measure === "value" ? formatValue : formatCount;
  const categories = months.map((m) => monthLabel(m.month));
  const figure = (m, s) => m.stages[s.key][measure] || 0;

  const option = {
    animation: false,
    textStyle: { color: colors.textPrimary, fontFamily: "inherit" },
    grid: { left: 8, right: 16, top: 40, bottom: 8, containLabel: true },
    legend: {
      data: drawn.map((s) => s.label),
      type: "scroll",
      top: 0,
      left: 0,
      icon: "roundRect",
      itemWidth: 12,
      itemHeight: 8,
      textStyle: { color: colors.textSecondary },
      pageTextStyle: { color: colors.textSecondary },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.grid,
      textStyle: { color: colors.textPrimary },
      formatter(params) {
        const i = params[0].dataIndex;
        const markers = {};
        params.forEach((p) => {
          markers[p.seriesName] = p.marker;
        });
        const title = `<strong>${categories[i]}</strong>${months[i].partial ? " (so far)" : ""}`;
        const rows = listed.map(
          (s) => `${markers[s.label] || ""}${s.label}: <strong>${format(figure(months[i], s))}</strong>`
        );
        return [title].concat(rows).join("<br/>");
      },
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: { lineStyle: { color: colors.axis } },
      axisTick: { show: false },
      axisLabel: {
        color: colors.textSecondary,
        hideOverlap: true,
        formatter: (label, i) => (months[i].partial ? `${label}\n(so far)` : label),
      },
    },
    yAxis: {
      type: "value",
      minInterval: measure === "count" ? 1 : undefined,
      splitLine: { lineStyle: { color: colors.grid } },
      axisLabel: { color: colors.textSecondary, formatter: measure === "value" ? compactValue : compact },
    },
    series: drawn.map((s) => ({
      name: s.label,
      type: "bar",
      itemStyle: { color: colors.series[STAGE_SLOTS[s.key]] },
      emphasis: { focus: "series" },
      data: months.map((m) => ({ value: figure(m, s), itemStyle: m.partial ? { opacity: 0.45 } : undefined })),
    })),
  };

  const table = {
    columns: drawn.map((s) => s.label),
    rows: months.map((m) => ({
      label: monthLabel(m.month),
      note: m.partial ? "so far" : "",
      cells: drawn.map((s) => format(figure(m, s))),
    })),
  };
  return { option, table };
}

/**
 * The same months with each stage's count and value as a running total from the first month, so
 * the last month equals the funnel summary. Lead and Contacted value stays null.
 */
export function runningTotals(months) {
  return months.map((m, i) => ({ ...m, stages: stageTotals(months.slice(0, i + 1)) }));
}

/** The conversion rates the Conversion % chart can show, the full funnel first. */
export const CONVERSION_RATES = [
  { value: "lead-closed", text: "Lead to closed (full)", from: "lead", to: "closed" },
  { value: "lead-qualified", text: "Lead to contacted", from: "lead", to: "qualified" },
  { value: "qualified-demo", text: "Contacted to demo", from: "qualified", to: "demo" },
  { value: "demo-proposal", text: "Demo to proposal", from: "demo", to: "proposal" },
  { value: "proposal-negotiation", text: "Proposal to negotiation", from: "proposal", to: "negotiation" },
  { value: "negotiation-closed", text: "Negotiation to closed", from: "negotiation", to: "closed" },
];

/**
 * One conversion rate per Reporting Month, from that month's cohort counts: the later stage's
 * count ÷ the earlier stage's. A month where the earlier stage is 0 reads 0% and is flagged
 * "No records" (shaded, and noted in the tooltip and table).
 *
 * @param {Object} opts
 * @param {Array<{ month, partial, stages }>} opts.months
 * @param {string} opts.rate  a CONVERSION_RATES value
 * @returns {{ option: Object|null, table: { columns: string[], rows: Array } }}
 */
export function conversionChart({ months, rate }) {
  if (!months.length) return { option: null, table: { columns: [], rows: [] } };
  const r = CONVERSION_RATES.find((x) => x.value === rate) || CONVERSION_RATES[0];
  const noRecords = months.map((m) => m.stages[r.from].count === 0);
  const values = months.map((m) => percent(m.stages[r.to].count, m.stages[r.from].count) || 0);
  const format = (v) => formatPercent(v, 1);
  const option = lineChartOption({
    months,
    series: [{ name: r.text, data: values, emphasis: true }],
    format,
    approximate: noRecords,
    approximateLabel: "No records",
  });
  const rows = months.map((m, i) => ({
    label: monthLabel(m.month),
    note: [m.partial ? "so far" : "", noRecords[i] ? "no records" : ""].filter(Boolean).join(", "),
    cells: [format(values[i])],
  }));
  return { option, table: { columns: [r.text], rows } };
}
